import { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameScene } from '@Core/GameScene/GameScene';
import { TimerEvent } from '@Core/Scene/Scene';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IPowerLine, IScatters, IWinningLine } from '@GFTypes/GameStoreTypes';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import { DeferredPromise, EDefferedPromiseState } from '@Tools/DeferredPromise';
import { CycleStep, CycleStepType, ISymbolsHighlightPresenter } from './SymbolsHighlighter.types';
import { ESymbolsHighlighterPhases } from './SymbolsHighlighterPhases';

export class SymbolsHighlighter {
    private readonly paylines: number[][] | undefined;
    private cycleSteps: CycleStep[] = [];
    private cycleIndex: number = 0;
    private readonly DEFAULT_CYCLE_DURATION = 1000;
    private distributedDuration: number | undefined;
    private currentStepPromises: ICancelablePromise[];
    private readonly terminatePromise: DeferredPromise<void> = new DeferredPromise();
    private readonly winPresentationBeforeExpandingDuration: number;
    private dynamicPhaseTimer: Partial<TimerEvent> | undefined;

    constructor(
        private readonly scene: GameScene,
        private readonly mainPresenter: ISymbolsHighlightPresenter,
        private readonly scattersPresenter?: ISymbolsHighlightPresenter
    ) {
        Logger.log(LoggerLevel.Constructor, { class: SymbolsHighlighter.name, source: LoggerSource.Game, message: 'SymbolsHighlighter constructor' });
        const gameStore = this.scene.game.gameStore;
        this.paylines = gameStore.get(EGameStoreKeys.AuthenticateGameConfig).payLines;
        EventsCenter.on(EGameStates.PRE_WIN_STEP_STATE, this.onPreWinStepState, this);
        EventsCenter.on(EGameStates.WIN_STATE, this.onWinState, this);
        EventsCenter.on(EGameStates.COMPLETE_WIN_STATE, this.stop, this);
        EventsCenter.on(EGameStates.POST_WIN_STEP_STATE, this.onPostWinStepState, this);
        EventsCenter.on(EGameStates.PRE_SPIN_STATE, this.stop, this);
        EventsCenter.on(EGameEvents.TAKE, this.stop, this);
        EventsCenter.on(EGameStates.POST_WIN_STATE, this.onPostWinState, this);
        EventsCenter.on(EGameEvents.SKIP, this.onSkip, this);
    }

    public getCurrentCycleSteps(): CycleStep[] {
        return this.cycleSteps;
    }

    private start(): void {
        this.terminatePromise.reset();
        this.cycleIndex = 0;
        if (this.distributedDuration) this.runDynamicCycle();
        else this.runNormalCycle();
    }

    private stop() {
        this.dynamicPhaseTimer?.destroy!();
        this.mainPresenter.hide();
        this.scattersPresenter?.hide();
        this.terminatePromise.resolve();
    }

    private onWinState() {
        if (this.cycleSteps.length <= 0) {
            const { isLastWinPresentationStep } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
            if (!isLastWinPresentationStep) StateConditions.winCondition.resolve();
            return;
        }
        this.start();
    }

    private onPostWinStepState() {
        const { isLastWinPresentationStep } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        if (isLastWinPresentationStep) return;
        this.stop();
        EventsCenter.emit(EGameEvents.STOP_SYMBOL_ANIMATIONS);
    }

    private async onPostWinState(): Promise<void> {
        if (this.cycleSteps.length <= 0) return;
        this.dynamicPhaseTimer?.destroy!();
        this.terminatePromise.reset();
        if (
            this.terminatePromise.state === EDefferedPromiseState.FULFILLED ||
            this.scene.game.gameStore.get(EGameStoreKeys.IsTakeInitiated) ||
            this.scene.game.gameStore.get(EGameStoreKeys.DebugWinPresentationModeState).nextWinPresentationRequested
        )
            return;
        this.runNormalCycle();
    }

    private onSkip() {
        const currentGameState = this.scene.game.gameStore.get(EGameStoreKeys.CurrentGameState);
        const isWinState = currentGameState === EGameStates.WIN_STATE;
        const { isLastWinPresentationStep } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        if (!isWinState || isLastWinPresentationStep) return;
        this.stop();
        StateConditions.winCondition.resolve();
    }

    private async runNormalCycle(): Promise<void> {
        while (this.terminatePromise.state === EDefferedPromiseState.PENDING) {
            const currentStep = this.cycleSteps[this.cycleIndex];
            currentStep.phase = ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE;
            this.currentStepPromises = this.showStep(currentStep);
            const waitForSymbolWinAnimations = this.waitForCancelablePromises(this.currentStepPromises);
            await Promise.race([waitForSymbolWinAnimations, this.terminatePromise.promise]);
            const { isLastWinPresentationStep } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
            if (!isLastWinPresentationStep && this.cycleIndex === this.cycleSteps.length - 1) {
                this.onNonLastWinStepCycleEnd();
                break;
            }
            this.cycleIndex = this.getNextCycleIndex(this.cycleIndex, this.cycleSteps.length);
        }
    }

    private runDynamicCycle(): void {
        let repeatCount = 0;
        this.dynamicPhaseTimer = this.scene.time.addEvent({
            delay: this.distributedDuration!,
            repeat: this.cycleSteps.length + 1,
            executeFirstImmediately: true,
            callback: () => {
                repeatCount++;
                const { isLastWinPresentationStep } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
                const lastCallback = repeatCount === this.cycleSteps.length + 1;
                if (lastCallback) {
                    if (!isLastWinPresentationStep) {
                        this.onNonLastWinStepCycleEnd();
                    }
                    return;
                }
                const currentStep = this.cycleSteps[this.cycleIndex];
                currentStep.phase = ESymbolsHighlighterPhases.DYNAMIC_CYCLE_PHASE;
                this.currentStepPromises = this.showStep(currentStep);
                this.cycleIndex = this.getNextCycleIndex(this.cycleIndex, this.cycleSteps.length);
            }
        });
    }

    private async onNonLastWinStepCycleEnd() {
        if (this.terminatePromise.state === EDefferedPromiseState.FULFILLED || this.scene.game.gameStore.get(EGameStoreKeys.IsTakeInitiated)) return;
        this.stop();
        StateConditions.winCondition.resolve();
    }

    private getNextCycleIndex(cycleIndex: number, cycleStepsLength: number): number {
        return (cycleIndex + 1) % cycleStepsLength;
    }

    private showStep(cycleStep: CycleStep): ICancelablePromise[] {
        this.mainPresenter.hide();
        this.scattersPresenter?.hide();
        this.currentStepPromises?.forEach((cancelablePromise) => cancelablePromise.cancel());

        switch (cycleStep.type) {
            case CycleStepType.MAIN:
                return this.mainPresenter.show(cycleStep);
            case CycleStepType.SCATTER:
                return this.scattersPresenter?.show(cycleStep) ?? [];
        }
    }

    private onPreWinStepState() {
        const { currentWinPresentationStepIndex } = this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        const { winningLines, powerLines, scatters, finalSymbols } =
            this.scene.game.gameStore.get(EGameStoreKeys.SpinOutcome).steps.at(currentWinPresentationStepIndex) ?? {};
        if (finalSymbols && (winningLines?.length || powerLines?.length || scatters?.length)) {
            const stepsNumber = (winningLines?.length ?? 0) + (powerLines?.length ?? 0) + (scatters?.length ?? 0);
            ({ distributedDuration: this.distributedDuration } = this.getDurations(stepsNumber));
            this.cycleSteps = this.buildCycleSteps(winningLines, powerLines, scatters);
        } else {
            this.cycleSteps = [];
        }
    }

    private getDurations(stepsNumber: number): { distributedDuration: number | undefined; longestAnimationDuration: number } {
        const { odometerDurationsPerWinLevel, finalWinLevelIndex, winPresentationBeforeExpandingDuration, isLastWinPresentationStep } =
            this.scene.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        const odometerDuration = odometerDurationsPerWinLevel[finalWinLevelIndex];
        const preFinalStepDuration = winPresentationBeforeExpandingDuration ?? odometerDuration;
        const allCyclesDuration = isLastWinPresentationStep ? odometerDuration : preFinalStepDuration;
        const mainPresenterAnimationDuration = this.mainPresenter.getAnimationDuration();
        const scattersPresenterAnimationDuration = this.scattersPresenter?.getAnimationDuration() ?? 0;
        const longestAnimationDuration = Math.max(mainPresenterAnimationDuration, scattersPresenterAnimationDuration);
        let distributedDuration = undefined;
        const durationPerStep = allCyclesDuration / stepsNumber;
        if (durationPerStep < longestAnimationDuration) distributedDuration = durationPerStep;
        return { distributedDuration, longestAnimationDuration };
    }

    private buildCycleSteps(winningLines: IWinningLine[] | undefined, powerLines: IPowerLine[] | undefined, scatters: IScatters[] | undefined): CycleStep[] {
        const steps: CycleStep[] = [];
        let cycleIndex = 0;

        let scatterIndex = 0;
        scatters?.forEach((scatter) => {
            if (!scatter.positions?.length) return;
            const { symbolId, selectionMask } = scatter;

            steps.push({
                type: CycleStepType.SCATTER,
                phase: ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE,
                selectionMask,
                cycleIndex: cycleIndex++,
                index: scatterIndex++,
                onlyInterchangeableSymbols: false,
                symbolId,
                multiplier: scatter.winMessageMultiplier
            });
        });

        let winningLinesIndex = 0;
        winningLines?.forEach((winline) => {
            const { id, symbolId, onlyInterchangeableSymbols, selectionMask } = winline;

            if (!symbolId) return;

            steps.push({
                type: CycleStepType.MAIN,
                phase: ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE,
                selectionMask,
                paylineId: id,
                payline: this.paylines?.[winline.id],
                cycleIndex: cycleIndex++,
                index: winningLinesIndex++,
                multiplier: winline.winMessageMultiplier,
                onlyInterchangeableSymbols: !!onlyInterchangeableSymbols,
                symbolId: winline.symbolId
            });
        });

        let powerlinesIndex = 0;
        powerLines?.forEach((powerLine) => {
            const { symbolId, onlyInterchangeableSymbols, selectionMask } = powerLine;
            if (!symbolId) return;

            steps.push({
                type: CycleStepType.MAIN,
                phase: ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE,
                selectionMask,
                cycleIndex: cycleIndex++,
                index: powerlinesIndex++,
                multiplier: powerLine.winMessageMultiplier,
                onlyInterchangeableSymbols: !!onlyInterchangeableSymbols,
                symbolId
            });
        });

        return steps;
    }

    private waitForCancelablePromises(promises: ICancelablePromise[]): Promise<(TrackEntry | void)[]> {
        return Promise.all((promises ?? []).map((p) => p.promise));
    }
}
